import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Typography, Box, CircularProgress } from '@mui/material';
import { useLanguage } from '../hooks/useLanguage';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

// Create a custom icon for user location
const userLocationIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'user-location-marker'
});

const Map = ({ 
  center, 
  zoom = 13, 
  title, 
  address, 
  showCircle = true, 
  checkedInUsers = [], 
  CheckInMarker = null,
  userLocation = null
}) => {
  const [isMapReady, setIsMapReady] = useState(false);
  const { t } = useLanguage();
  
  // Force map to update size when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      setIsMapReady(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);
  
  if (!center || !center.lat || !center.lng) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100%" 
        width="100%"
      >
        <Typography>No location coordinates available</Typography>
      </Box>
    );
  }
  
  // Ensure Leaflet is available in the browser environment
  if (typeof window === 'undefined' || !window.L) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100%" 
        width="100%"
      >
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  return (
    <MapContainer
      key={`map-${center.lat}-${center.lng}-${isMapReady ? 'ready' : 'loading'}`}
      center={[center.lat, center.lng]}
      zoom={zoom}
      style={{ height: '100%', width: '100%' }}
      zoomControl={false}
      attributionControl={false}
      scrollWheelZoom={false}
    >
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      
      <Marker position={[center.lat, center.lng]}>
        <Popup>
          {title && <Typography variant="body2" fontWeight="bold">{title}</Typography>}
          {address && <Typography variant="body2">{address}</Typography>}
        </Popup>
      </Marker>
      
      {showCircle && (
        <Circle
          center={[center.lat, center.lng]}
          radius={300}
          pathOptions={{
            fillColor: '#3f51b5',
            fillOpacity: 0.2,
            color: '#3f51b5',
            weight: 2
          }}
        />
      )}
      
      {/* Display user's current location if available */}
      {userLocation && userLocation.lat && userLocation.lng && (
        <>
          <Marker 
            position={[userLocation.lat, userLocation.lng]} 
            icon={userLocationIcon}
          >
            <Popup>
              <Typography variant="body2" fontWeight="bold">{t.yourLocation}</Typography>
              {userLocation.accuracy && (
                <Typography variant="body2">
                  {t.accuracy}: {Math.round(userLocation.accuracy)} {t.meters}
                </Typography>
              )}
            </Popup>
          </Marker>
          
          {userLocation.accuracy && (
            <Circle
              center={[userLocation.lat, userLocation.lng]}
              radius={userLocation.accuracy}
              pathOptions={{
                fillColor: '#4caf50',
                fillOpacity: 0.1,
                color: '#4caf50',
                weight: 1
              }}
            />
          )}
        </>
      )}
      
      {CheckInMarker && checkedInUsers && checkedInUsers.length > 0 && 
        checkedInUsers.map((checkIn) => {
          // Ensure the position is valid
          if (!checkIn.position || !checkIn.position.lat || !checkIn.position.lng) {
            return null;
          }
          
          return (
            <CheckInMarker
              key={checkIn.userId}
              position={checkIn.position}
              userName={checkIn.userName}
              isHost={checkIn.isHost}
              accuracy={checkIn.position.accuracy}
            />
          );
        })
      }
    </MapContainer>
  );
};

export default Map; 